import { Assistant, AssistantData, AssistantFormValues, WhatsAppConfigParams, WhatsAppConfigValues, Flow, FlowConversation, FlowAction } from '../types';
import { HttpRequest } from '../../../utils/httpClient';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('features-assistants');

const API_BASE_URL = process.env.REACT_APP_API_HOST || 'https://api.dev.mind.creai.cloud';

// Servicios relacionados con la gestión de asistentes
export const myAssistantServices = {
  // Flow Services
  createFlow: async (flowData: { name: string; description: string; chatbotId: string; status: number }): Promise<Flow> => {
    const flowRequest = new HttpRequest(`${API_BASE_URL}/flows`);
    const response = await flowRequest.post(JSON.stringify(flowData));
    return response.data.flowData;
  },

  updateFlow: async (flowId: string, flowData: { name: string; description: string; chatbotId: string; status: number }): Promise<void> => {
    const flowRequest = new HttpRequest(`${API_BASE_URL}/flows`);
    await flowRequest.put(`${flowId}`, flowData);
  },

  createFlowConversation: async (flowId: string, messages: Array<{ step: number; message: string; role: string }>): Promise<void> => {
    const conversationRequest = new HttpRequest(`${API_BASE_URL}/flows/conversation`);
    await conversationRequest.post(JSON.stringify({ flowId, messages }));
  },

  updateFlowConversation: async (flowId: string, messages: Array<{ step: number; message: string; role: string }>): Promise<void> => {
    const conversationRequest = new HttpRequest(`${API_BASE_URL}/flows/conversation`);
    await conversationRequest.put(`${flowId}`, { messages });
  },

  createFlowAction: async (actionData: {
    integrationFunctionId: string;
    authId: string;
    parameters: any;
    chatbotId: string;
    flowId: string;
  }): Promise<FlowAction> => {
    const actionRequest = new HttpRequest(`${API_BASE_URL}/flows/actions`);
    const response = await actionRequest.post(JSON.stringify(actionData));
    return response.data.action;
  },

  updateFlowAction: async (actionId: string, actionData: {
    integrationFunctionId: string;
    authId: string;
    parameters: any;
    chatbotId: string;
    flowId: string;
  }): Promise<FlowAction> => {
    const actionRequest = new HttpRequest(`${API_BASE_URL}/flows/actions`);
    const response = await actionRequest.put(`${actionId}`, actionData);
    return response.data.action;
  },

  generateAIFlow: async (prompt: string, chatbotId: string, fileContent?: string): Promise<any> => {
    try {
      const requestData: any = {
        chatbotId
      };

      // Include prompt if provided and not empty
      if (prompt && prompt.trim().length > 0) {
        requestData.prompt = prompt.trim();
      }

      // Include file content if provided and not empty
      if (fileContent && fileContent.trim().length > 0) {
        requestData.fileContent = fileContent.trim();
      }

      const response = await fetch(`${process.env.REACT_APP_API_HOST}/flows/GenerateFlow`, {
      //const response = await fetch(`http://localhost:3000/GenerateFlow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      logger.error('Error generating AI flow:', error);
      throw error;
    }
  },

  getIntegrations: async (): Promise<any> => {
    const request = new HttpRequest(`${API_BASE_URL}/integration`);
    const response = await request.get('');
    return response.data.integrations;
  },

  getFlowActions: async (functionId: string): Promise<any> => {
    const actionsRequest = new HttpRequest(`${API_BASE_URL}/flows/actions/integration-function`);
    const response = await actionsRequest.get(`${functionId}`);
    return response.data.actions;
  },

  getWorkflows: async (assistantId: string): Promise<any> => {
    const workflowsRequest = new HttpRequest(`${API_BASE_URL}/workflows`);
    const response = await workflowsRequest.get(`?assistantId=${assistantId}`);
    return response.data;
  },

  getWorkflowExecutions: async (workflowId: string): Promise<any> => {
    const executionsRequest = new HttpRequest(`${API_BASE_URL}/v1/workflows/${workflowId}/executions`);
    const response = await executionsRequest.get('');
    return response.data;
  },

  saveCustomIntegration: async (data: {
    integrationName: string;
    generatedCode: string;
    chatbotId: string;
    flowId?: string;
    metadata?: any;
  }): Promise<{id: string}> => {
    const request = new HttpRequest(`${process.env.REACT_APP_API_HOST}/integration/save-custom-integration`);
    //const request = new HttpRequest(`http://localhost:3000/save-custom-integration`);
    const response = await request.post(JSON.stringify(data));
    return response.data.response;
  },

  getCustomIntegrations: async (chatbotId: string): Promise<any[]> => {
    try {
      const request = new HttpRequest(`${process.env.REACT_APP_API_HOST}/integration/custom-integrations`);
      const response = await request.get(`?chatbotId=${chatbotId}`);
      return response.data.response?.integrations || [];
    } catch (error) {
      logger.error('Error fetching custom integrations:', error);
      return [];
    }
  },

  deleteCustomIntegration: async (integrationId: string): Promise<boolean> => {
    try {
      const request = new HttpRequest(`${process.env.REACT_APP_API_HOST}/integration/custom-integrations`);
      await request.delete(integrationId);
      return true;
    } catch (error) {
      logger.error('Error deleting custom integration:', error);
      return false;
    }
  },

  /**
   * Gets the chatbot ID for a specific flow type from system prompts
   * @param flowType - The type of flow (personality, conversation, process, etc.)
   * @returns The chatbot ID or null if not found
   */
  getChatbotIdByFlowType: async (flowType: string): Promise<string | null> => {
    
    try {
      const request = new HttpRequest(`${API_BASE_URL}/generator/systemprompts`);
      const response = await request.get(`?flowType=${flowType}`);
logger.info("getChatbotIdByFlowType response", response);
      if (response.data && response.data.prompts && response.data.prompts.length > 0) {
        return response.data.prompts[0].chatbotId || null;
      }

      return null;
    } catch (error) {
      logger.error(`Error fetching chatbot ID for flowType ${flowType}:`, error);
      return null;
    }
  },

  // Fetch Process Flow Generator chatbot configuration
  getChatbotConfig: async (chatbotId: string) => {

      const configRequest = new HttpRequest(`${process.env.REACT_APP_API_HOST}/chatbot`);
      const response = await configRequest.get(chatbotId);
      if (response.status === 200) {
        return response.data;
      }else{
      logger.error('[Process Flow Generator] Set fallback config if fetch fails');
      // Set fallback config if fetch fails
      return{
        botName: 'Process Flow Generator',
        colorMain: '#7E70B3',
        colorTitle: '#E3F571',
        iconBgColor: '#ffffff'
      };
    }
  },

  // Tool Schema Services
  createToolSchema: async (toolSchemaData: any): Promise<{ id: string }> => {
    const toolSchemaRequest = new HttpRequest(`${API_BASE_URL}/chatbot/tool-schema`);
    const response = await toolSchemaRequest.post(JSON.stringify(toolSchemaData));
    return response.data;
  },

  updateToolSchema: async (toolSchemaId: string, toolSchemaData: any): Promise<{ id: string }> => {
    const toolSchemaRequest = new HttpRequest(`${API_BASE_URL}/chatbot/tool-schema`);
    const response = await toolSchemaRequest.put(toolSchemaId, toolSchemaData);
    return response.data;
  },

  listToolSchemas: async (chatbotId: string): Promise<any[]> => {
    const toolSchemaRequest = new HttpRequest(`${API_BASE_URL}/chatbot/tool-schema`);
    const response = await toolSchemaRequest.get('', { chatbotId });
    return Array.isArray(response.data) ? response.data : [];
  },

  deleteToolSchema: async (toolSchemaId: string): Promise<void> => {
    const toolSchemaRequest = new HttpRequest(`${API_BASE_URL}/chatbot/tool-schema`);
    await toolSchemaRequest.delete(toolSchemaId);
  },
};
